import { Signal } from '@lit-labs/signals'
import { interpolate } from './interpolate'

/**
 * Every word a schmancy component renders on its own behalf — chip labels,
 * field labels, aria descriptions, validation messages — with its English
 * wording, keyed `<component>.<slot>`. `{{param}}` placeholders are filled by
 * the component at the point of use.
 *
 * This is the whole vocabulary and the only language in it. The library owns
 * the key set and the English; an app owns every other language and hands one
 * in through `setComponentText`. That line is what keeps the library out of
 * deciding which languages exist and who maintains them.
 *
 * A component reads a key with `componentText(key, params?)` inside `render()`;
 * an app swaps the whole table whenever its language changes — at boot and at
 * every switch after it, since a switch has to reach chrome already on screen,
 * including a panel an overlay currently has open. Mounted components re-render
 * on the swap, so a component that reads its wording at render time follows it
 * with no further wiring; one that writes words imperatively (into a live
 * region, or onto a ref) needs its own reason to re-run, or its text stays in
 * the outgoing language until something else moves it.
 *
 * ```ts
 * import { setComponentText } from '@mhmo91/schmancy'
 * locale$.subscribe(lang => setComponentText(lang === 'de' ? GERMAN : {}))
 * ```
 */
export const componentTextDefaults = {
	'thinking-bubble.invalidSvg': 'Enter valid SVG markup.',
	'thinking-bubble.undrawableSvg': 'The SVG could not be drawn. Use a self-contained SVG.',
	'thinking-bubble.unloadedSvg': 'The SVG could not load. Check its markup.',
	'neural-brain.drawing': 'Drawing {{label}}',
	'neural-brain.within': 'in {{parent}}',
	'neural-brain.status.done': 'done',
	'neural-brain.status.running': 'running',
	'neural-brain.status.waiting': 'waiting',
	'neural-brain.status.failed': 'failed',
	'execution.map': 'Execution map',
	'execution.mapDescription': 'Definitions and observed execution transitions.',
	'execution.keys': 'Press the up and down arrows to move between steps, and Enter to open one. Drag or scroll to move. Pinch, or press plus and minus, to zoom. Press 0 to fit.',
	'execution.steps': 'Steps',
	'execution.loading': 'Loading steps',
	'execution.loaded': 'Steps loaded',
	'execution.rowNumbered': '{{number}}. {{label}}',
	'execution.sentBack.title': 'Sent back with a reason',
	'execution.sentBack.to': 'To step {{number}}',
	'execution.sentBack.name': 'Sent back with a reason, to step {{number}}: {{reason}}',
	'execution.sentBack.nameBare': 'Sent back to step {{number}}',
	'execution.overview': 'Overview',
	'execution.summary.step': '1 step',
	'execution.summary.steps': '{{count}} steps',
	'execution.summary.modelCall': '1 model call',
	'execution.summary.modelCalls': '{{count}} model calls',
	'execution.summary.sentBackOnce': 'sent back once',
	'execution.summary.sentBackTwice': 'sent back twice',
	'execution.summary.sentBackTimes': 'sent back {{count}} times',
	'execution.tokens': '{{count}} tokens',
	'execution.costAtLeast': '≥ {{cost}}',
	'execution.notPriced': 'not priced',
	'execution.usageNotRecorded': 'usage not recorded',
	'execution.durationSeconds': '{{duration}} s',
	'execution.zoomIn': 'Zoom in on {{name}}',
	'execution.zoomOut': 'Zoom out of {{name}}',
	'execution.fit': 'Fit {{name}} to view',
	'execution.nothingReached': 'Nothing has run yet',
	'execution.details': '{{name}} execution details',
	'execution.close': 'Close node details',
	'execution.position': 'Step {{index}} of {{total}}',
	'execution.previous': 'Previous',
	'execution.next': 'Next',
	'execution.whatHappened': 'What happened',
	'execution.attemptOf': 'attempt {{attempt}} of {{total}}',
	'execution.attemptChip': 'Attempt {{attempt}}',
	'execution.reason': 'Reason',
	'execution.retry': 'Retry',
	'execution.loadingDetails': 'Loading {{name}} details',
	'execution.loadedDetails': '{{name}} details loaded',
	'execution.definitionOnly': 'Not run · definition only',
	'execution.attempt': '{{status}} · attempt {{attempt}}',
	'execution.contract': 'Configured input contract',
	'execution.occurrences': 'Reached occurrences',
	'execution.flow': 'Input → Goal, decision and evidence → Output',
	'execution.flowDescription': 'Input, decision and output',
	'execution.input': 'Input',
	'execution.output': 'Output',
	'execution.goal': 'Goal',
	'execution.decision': 'Decision summary',
	'execution.evidence': 'Evidence',
	'execution.prompt': 'Prompt',
	'execution.error': 'Error',
	'execution.unrecorded': 'Not recorded',
	'execution.noInput': 'Not run · no input recorded',
	'execution.noOutput': 'Not run · no output recorded',
	'execution.model': 'Model',
	'execution.elapsed': 'Duration',
	'execution.usage': 'Usage',
	'execution.input.input': 'Source',
	'execution.output.input': 'Submitted content',
	'execution.input.agent': 'Request',
	'execution.output.agent': 'Response',
	'execution.input.capability': 'Arguments',
	'execution.output.capability': 'Result',
	'execution.input.tool': 'Arguments',
	'execution.output.tool': 'Result',
	'execution.input.model': 'Model input',
	'execution.output.model': 'Model output',
	'execution.input.memory': 'Lookup',
	'execution.output.memory': 'Retrieved memory',
	'execution.input.question': 'Question context',
	'execution.output.question': 'Answer',
	'execution.input.effect': 'Requested change',
	'execution.output.effect': 'Change result',
	'execution.input.receipt': 'Recorded request',
	'execution.output.receipt': 'Receipt',
	'execution.input.read': 'Query',
	'execution.output.read': 'Read result',
	'execution.input.runtime': 'Trigger',
	'execution.output.runtime': 'Runtime result',
	'execution.metadata': 'Model: {{model}} · Duration: {{duration}} · Usage: {{usage}}',
	'execution.duration': '{{duration}} ms',
	'execution.payloadSubject': '{{name}} · {{field}}',
	'execution.status.pending': 'Not run',
	'execution.status.running': 'Running',
	'execution.status.completed': 'Done',
	'execution.status.waiting': 'Waiting for an answer',
	'execution.status.failed': 'Failed',
	'execution.status.sentBack': 'Sent back',
	'execution.status.leftOpen': 'Left open',
	'execution.availability.available': 'Available',
	'execution.availability.dormant': 'Not available',
	'execution.kind.input': 'Input',
	'execution.kind.read': 'Read',
	'execution.kind.agent': 'Agent',
	'execution.kind.capability': 'Capability',
	'execution.kind.tool': 'Tool call',
	'execution.kind.model': 'Model call',
	'execution.kind.memory': 'Memory',
	'execution.kind.question': 'Question',
	'execution.kind.effect': 'Change',
	'execution.kind.receipt': 'Receipt',
	'execution.kind.runtime': 'Run',
	'autocomplete.search': 'Search options',
	'autocomplete.cancel': 'Cancel',
	'autocomplete.apply': 'Apply',
	'autocomplete.empty': 'No results for “{{query}}”',
	'autocomplete.required': 'Please select an option.',
	'overlay.restoreViewer': 'Restore viewer',
	'overlay.maximizeViewer': 'Maximize viewer',
	'overlay.closeViewer': 'Close viewer',
	'overlay.close': 'Close',
	'overlay.closeNamed': 'Close {{label}}',
	'voiceNote.name': 'Voice note, {{length}}',
	'voiceNote.play': 'Play voice note',
	'voiceNote.pause': 'Pause voice note',
	'voiceNote.unplayed': 'Not played yet',
	'voiceNote.transcript': 'Transcript',
	'voiceNote.remove': 'Remove voice note, {{length}}',
	'voiceNote.failed': 'Audio could not play. Try again.',
	'messageComposer.message': 'Message',
	'messageComposer.send': 'Send message',
	'messageComposer.recordVoice': 'Record a voice note',
	'messageComposer.keepVoice': 'Stop and keep this recording',
	'messageComposer.pauseVoice': 'Pause recording',
	'messageComposer.resumeVoice': 'Resume recording',
	'messageComposer.pausedVoice': 'Recording paused',
	'messageComposer.discardVoice': 'Discard this recording',
	'messageComposer.recording': 'Recording your voice',
	'messageComposer.slideHint': '‹ Slide to cancel · slide up to lock',
	'messageComposer.micBlocked': 'Microphone blocked. Allow it in your browser settings, or type instead.',
	'messageComposer.recordingFailed': 'Recording could not start. Try recording again, or type instead.',
	'messageComposer.recordingEmpty': 'No audio was captured. Try recording again.',
	'messageComposer.recordingInterrupted': 'Recording stopped unexpectedly. The captured audio is kept in your draft.',
	'messageComposer.attach': 'Attach files',
	'messageComposer.dropFiles': 'Drop to attach',
	'messageComposer.adding': 'Adding files…',
	// The cap is one fact about the message, so it is said once however many
	// files of a batch met it.
	'messageComposer.refusal.tooMany.one': 'A message holds one file.',
	'messageComposer.refusal.tooMany.other': 'A message holds at most {{max}} files.',
	'messageComposer.refusal.failed': 'A file could not be added. Try again.',
	'fileChips.files': 'Attached files',
	'window.switcher': 'Open windows',
	'window.unnamed': 'Window',
	'window.details': 'Expandable content',
	'window.moveHint': 'Drag to move, click to expand',
	'window.close': 'Close window',
	'window.closeTab': 'Close {{label}}',
	'window.collapse': 'Collapse window',
	'window.expand': 'Expand window',
	'fileStack.files': '{{count}} files',
	'fileStack.open': 'Show {{count}} files',
	'fileStack.close': 'Collapse files',
	'csv.loading': 'Loading CSV preview',
	'csv.failed': 'Could not load CSV preview',
	'csv.retry': 'Retry',
	'csv.empty': 'This CSV file is empty',
	'csv.search': 'Search rows',
	'csv.count': '{{count}} rows · {{columns}} columns',
	'csv.matches': '{{count}} of {{total}} rows · {{columns}} columns',
	'csv.wrap': 'Wrap text',
	'csv.header': 'First row is header',
	'csv.preview': 'CSV preview',
	'csv.row': 'Source row',
	'csv.column': 'Column {{number}}',
	'csv.noMatches': 'No matching rows',
	'csv.clear': 'Clear search',
	'xml.loading': 'Reading XML document',
	'xml.failed': 'XML preview unavailable',
	'xml.retry': 'Retry preview',
	'xml.unsafe':
		'This XML contains external instructions or entities and cannot be previewed safely. Use the original file with your invoice software.',
	'xml.malformed': 'This file is not well-formed XML. Check the original file with the sender.',
	'xml.tooLarge': 'This XML exceeds the 2 MiB preview limit. Open the original file in your invoice software.',
	'xml.unreadable': 'The text encoding could not be read. Check the original file with the sender.',
	'xml.requestFailed': 'The original XML could not be loaded. Check your connection and retry.',
	'xml.view': 'XML document view',
	'xml.invoiceView': 'Invoice',
	'xml.source': 'XML source',
	'xml.sourceDescription': 'Original XML text',
	'xml.invoice': 'Invoice',
	'xml.document': 'Document',
	'xml.documentType': 'Document type code',
	'xml.creditNote': 'Credit note',
	'xml.numberMissing': 'Invoice number not provided',
	'xml.nameMissing': 'Name not provided',
	'xml.issued': 'Issued',
	'xml.dueDate': 'Due date',
	'xml.amountDue': 'Amount due',
	'xml.seller': 'Seller',
	'xml.buyer': 'Buyer',
	'xml.vatId': 'VAT ID',
	'xml.taxId': 'Tax ID',
	'xml.buyerReference': 'Buyer reference',
	'xml.orderReference': 'Order reference',
	'xml.items': 'Invoice items',
	'xml.description': 'Description',
	'xml.quantity': 'Quantity',
	'xml.unitPrice': 'Unit price',
	'xml.taxRate': 'Tax',
	'xml.lineNet': 'Net amount',
	'xml.noItems': 'No invoice items were found. See the XML source for the full document.',
	'xml.itemRange': 'Items {{start}}–{{end}} of {{total}}',
	'xml.previousItems': 'Previous items',
	'xml.nextItems': 'Next items',
	'xml.payment': 'Payment',
	'xml.paymentMeans': 'Payment means code',
	'xml.paymentReference': 'Payment reference',
	'xml.paymentTerms': 'Payment terms',
	'xml.iban': 'IBAN',
	'xml.bic': 'BIC',
	'xml.accountName': 'Account holder',
	'xml.accountId': 'Payment account',
	'xml.bankIdentifier': 'Bank identifier',
	'xml.totals': 'Invoice totals',
	'xml.net': 'Net total',
	'xml.tax': 'Tax amount',
	'xml.gross': 'Gross total',
	'xml.prepaid': 'Prepaid amount',
	'xml.allowance': 'Document discounts',
	'xml.charge': 'Document charges',
	'xml.taxBreakdown': 'Tax breakdown',
	'xml.taxBasis': 'Taxable amount',
	'xml.notes': 'Notes',
	'xml.readingNotice':
		'Values are read from the XML. This preview does not validate the invoice; XML source contains the full document.',
	'xml.thumbnail': 'XML preview of {{name}}',
	'pdfThumbnail.unavailable': 'Preview unavailable',
	// <schmancy-zoom-stage>, the file viewer's content stage. Its own namespace
	// rather than the viewer's: the stage serves the two mimes that zoom, and a
	// key called `fileViewer.zoomIn` would be a lie on the video and audio
	// branches the stage never reaches.
	//
	// `roledescription` is what a screen reader says INSTEAD of "group", so it is
	// per mime — a photograph announced as a document is a wrong fact, not a
	// vague one. `file` is the name a caller who named nothing gets, and it is
	// the stage's accessible name, so it is never blank.
	'zoomStage.document': 'Document',
	'zoomStage.image': 'Image',
	'zoomStage.file': 'File',
	'zoomStage.failed': 'This file could not be read.',
	'zoomStage.decodeFailed': 'The file could not be decoded.',
	// One page's text, not the file: the page's own ink is drawn and readable,
	// and only the layer a screen reader and find-in-page use is missing — so a
	// reader who cannot see that ink is the one person the console cannot tell.
	'zoomStage.pageTextFailed': 'Page {{page}}: its text could not be read.',
	'zoomStage.loading': 'Loading {{name}}',
	// Only above one page: a single page announces its own name and nothing
	// about counting, so English's own plural never has to be formed here.
	'zoomStage.pages': '{{name}}, {{count}} pages',
	'zoomStage.page': 'Page {{page}} of {{total}}',
	'zoomStage.zoomed': 'Zoomed to {{percent}} percent',
	'zoomStage.zoomIn': 'Zoom in',
	'zoomStage.zoomOut': 'Zoom out',
	'zoomStage.keys':
		'Pinch or double-tap to zoom. Drag to move. Plus and minus zoom, zero fits, arrows and page keys move through the file.',
	'sortPicker.ascending': 'Ascending',
	'sortPicker.descending': 'Descending',
	'sortPicker.ascendingChoice': '{{field}}, ascending',
	'sortPicker.descendingChoice': '{{field}}, descending',
	'dateRange.placeholder': 'Select date range',
	'dateRange.trigger': 'Select date range. Current: {{range}}',
	'dateRange.previous': 'Previous {{range}}',
	'dateRange.next': 'Next {{range}}',
	'dateRange.announce': 'Date range: {{range}}',
	'dateRange.from': 'From',
	'dateRange.to': 'To',
	'dateRange.presets': 'Date range presets',
	'dateRange.startOnly': 'From {{date}}',
	'dateRange.endOnly': 'To {{date}}',
	'dateRange.preset.today': 'Today',
	'dateRange.preset.yesterday': 'Yesterday',
	'dateRange.preset.last7': 'Last 7 days',
	'dateRange.preset.last30': 'Last 30 days',
	'dateRange.preset.thisWeek': 'This week',
	'dateRange.preset.thisMonth': 'This month',
	'dateRange.preset.lastMonth': 'Last month',
	'dateRange.preset.thisYear': 'This year',
	'dateRange.error.incomplete': 'Please select a complete date range.',
	'dateRange.error.endBeforeStart': 'End date must be on or after start date.',
	'dateRange.error.beforeMin': 'Start date must be on or after {{min}}.',
	'dateRange.error.afterMax': 'End date must be on or before {{max}}.',
	// <schmancy-date-range-inline>, the two-field sibling. Its endpoint labels ARE
	// the range field's — it reads `dateRange.from`/`dateRange.to` above rather
	// than minting a second pair — so one wording moves both controls. What
	// follows is only what this component says and the range field never does:
	// per-field required messages (it paints two fields, not one trigger) and the
	// gap/auto-correct rules that are its whole reason to exist.
	'dateRangeInline.error.startMissing': 'Start date is required',
	'dateRangeInline.error.endMissing': 'End date is required',
	'dateRangeInline.error.invalidFormat': 'Invalid date format',
	'dateRangeInline.error.endBeforeStart': 'End date must be after start date',
	'dateRangeInline.error.sameDate': 'Start and end dates cannot be the same',
	// One key per plural form, the count inside the sentence, because a noun
	// pluralized by the component and dropped into a foreign sentence inflects for
	// English and nothing else. English takes `.other` above one, which is the
	// branch the component's own count test already makes.
	'dateRangeInline.error.minGap.one': 'Minimum {{count}} day required between dates',
	'dateRangeInline.error.minGap.other': 'Minimum {{count}} days required between dates',
	'dateRangeInline.error.maxGap.one': 'Maximum {{count}} day allowed between dates',
	'dateRangeInline.error.maxGap.other': 'Maximum {{count}} days allowed between dates',
	'dateRangeInline.error.beforeMin': 'Date is before minimum allowed date',
	'dateRangeInline.error.afterMax': 'Date is after maximum allowed date',
	// <schmancy-date-field>. `placeholder` is both the trigger's visible text with
	// no value set and the name the trigger falls back to with no label set —
	// one slot, "what this control is called when nobody named it", the way
	// `dateRange.placeholder` already reaches both of the range trigger's.
	'dateField.placeholder': 'Select date',
	// The trigger's whole accessible name, in two shapes rather than one plus a
	// concatenated tail: the hint or error joins the sentence, and a language that
	// joins clauses with something other than a full stop can only say so if it
	// owns the join.
	'dateField.trigger': '{{label}}. Current: {{date}}',
	'dateField.triggerDescribed': '{{label}}. Current: {{date}}. {{description}}',
	'dateField.announce': 'Date: {{date}}',
	// The picker panel's own controls. Nothing the panel touches leaves it until
	// `save`, so these two are the commit and the discard, not a close button and
	// a second close button — the wording has to say which one keeps the work.
	'dateField.save': 'Save',
	'dateField.cancel': 'Cancel',
	// The one-tap whole-draft preset. Two keys rather than one filled with a
	// noun: "now" and "today" are different words in most languages and only one
	// of them is ever right for a given mode.
	'dateField.preset.now': 'Now',
	'dateField.preset.today': 'Today',
	// The refusal beside the disabled Save, and the control that repairs it in
	// one tap. `{{date}}` is already localized through `Intl`, so each key owns
	// its whole sentence and a language that puts the bound first can say so.
	'dateField.outOfRange.tooEarly': 'Earliest allowed is {{date}}',
	'dateField.outOfRange.tooLate': 'Latest allowed is {{date}}',
	'dateField.useEarliest': 'Use {{date}}',
	'dateField.useLatest': 'Use {{date}}',
	// The draft bar's in-place HH:mm readout — a field whose name is the segment
	// it takes, since the day beside it is already spelled out in full.
	'dateField.timeOfDay': 'Time of day',
	'dateField.dateEntry': 'Date (YYYY-MM-DD)',
	'dateField.dateEntryPlaceholder': 'YYYY-MM-DD',
	'dateField.invalidDate': 'Enter a valid date as YYYY-MM-DD.',
	'dateField.error.valueMissing': 'Please pick a date.',
	// `{{min}}`/`{{max}}` arrive as the raw `min`/`max` this field was given —
	// unlike `dateRange.error.beforeMin`, which formats its bound through
	// `Intl`. Both keys own their whole sentence either way, so a translation
	// reads correctly under both.
	'dateField.error.beforeMin': 'Date must be on or after {{min}}.',
	'dateField.error.afterMax': 'Date must be on or before {{max}}.',
	// <schmancy-time-picker>'s two rails. Its own namespace, not the date field's:
	// the picker is a primitive a consumer mounts on its own, and a key called
	// `dateField.hours` would be a lie on every surface that is not one.
	'timePicker.hours': 'Hour',
	'timePicker.minutes': 'Minutes',
	'timePicker.seconds': 'Seconds',
	'calendar.grid': 'Calendar',
	// The day cell's whole accessible name, not a suffix bolted onto a date the
	// component composes itself: `{{date}}` is already localized by `Intl`, and a
	// language that puts the state first, or joins it with something other than a
	// comma, can only say so if it owns the sentence.
	'calendar.day.selected': '{{date}}, selected',
	'calendar.day.rangeStart': '{{date}}, range start',
	'calendar.day.rangeEnd': '{{date}}, range end',
	'calendar.day.rangeStartPending': '{{date}}, range start selected, choose an end date',
	'calendar.day.today': '{{date}}, today',
	// The form-field mixin's own message — every field built on it renders it, so
	// it is the most-rendered wording in the library. A field's `errorMessages`
	// prop is still the per-instance override and still wins; the table is what it
	// falls back to, so an app that sets only the table localizes every field it
	// never touched.
	'formField.error.valueMissing': 'This field is required',
	// A checkbox and a switch each name what the reader must do, which the
	// generic line above cannot. `errorMessages.valueMissing` still wins over both.
	'checkbox.error.valueMissing': 'Please check this box if you want to proceed.',
	'switch.error.valueMissing': 'This switch is required.',
	// <schmancy-revolute>. The pay action's three labels. `payLabel` and
	// `retryPayLabel` are the host's per-instance seam and win over the table
	// they fall back to, so a CTA carrying the order total keeps carrying it.
	// The in-flight text has no prop: it is a state rather than an act. All
	// three are written onto the button's ref when the pay action's own state
	// moves rather than read at render, so the label holds the language current
	// at that move — a swap mid-checkout reaches it only once the state moves
	// again. `noticeCopy` is the failure panel's own seam and falls back to
	// `payment.notice.*` below.
	'payment.processing': 'Processing...',
	'payment.retry': 'Try again',
	'payment.pay': 'Pay',
	// The divider between payment methods. Two of them render it, and a divider
	// whose neighbours resolved away collapses by height rather than leaving the
	// DOM — so the collapsed one still sits in the accessibility tree and still
	// reads whatever this key says.
	'payment.or': 'Or',
	// Visually-hidden narration of a silent re-price rebuild, while `inert` has
	// taken the wallet buttons out of the tab order with no other explanation.
	'payment.announce.updating': 'Updating payment options…',
	// The receipt-email field, rendered only when the host does not already own
	// a contact field. It carries no prop of any kind, so these four are the
	// only words it can read. The two messages are assigned when validation
	// fails rather than read at render, so each holds the language current at
	// the keystroke that failed — a swap mid-checkout reaches the label and the
	// placeholder and leaves a standing message behind.
	'payment.email.label': 'Email address (for receipt)',
	'payment.email.placeholder': 'your@email.com',
	'payment.email.error.required': 'Email address is required',
	'payment.email.error.invalid': 'Please enter a valid email address',
	// The failure panel, and the largest block of wording in the library: every
	// word a buyer reads when their card is declined. Keyed
	// `payment.notice.<failure>.<slot>` — the failure is a `SchmancyRevoluteFailure`
	// member spelled verbatim and the slot is a `SchmancyRevoluteNoticeText` field,
	// so a key stands one-to-one against the entry the `noticeCopy` prop overrides
	// it with (`noticeCopy.expiredCard.headline` ↔ `payment.notice.expiredCard.headline`)
	// and the widget builds the key from the failure rather than holding a second
	// table that could name the wrong one.
	//
	// The axis is what the buyer can DO, never what the vendor called it: the
	// vendor's own sentence is telemetry and never reaches a screen. Every body
	// carries the fact that no money moved, because that is the buyer's first
	// question and the one the vendor's string never answers — a translation that
	// drops it answers a different question than the English does.
	//
	// Only `blocked` takes a `remedy`: it is the one failure the widget itself can
	// recover from, by re-entering its own initialization. `orderStale` disables
	// the pay action alongside it and still takes none, since that recovery belongs
	// to the host.
	//
	// Read when the notice FIRES, not at render — the widget hands the panel a
	// resolved object once, so the words hold the language current at the failure.
	// A swap mid-checkout reaches the next failure and leaves a panel already on
	// screen in the outgoing language, the same limitation the pay action's three
	// labels above carry for the same reason.
	'payment.notice.insufficientFunds.headline': "Your card didn't have enough funds",
	'payment.notice.insufficientFunds.body':
		'Nothing was charged. Try a different card, or top this one up and try again.',
	'payment.notice.expiredCard.headline': 'That card has expired',
	'payment.notice.expiredCard.body': 'Nothing was charged. Use a different card to finish your order.',
	'payment.notice.cardDetails.headline': "Some of the card details didn't match",
	'payment.notice.cardDetails.body':
		'Nothing was charged. Check the card number, expiry date and security code, then try again.',
	'payment.notice.contactDetails.headline': "We couldn't use those contact details",
	'payment.notice.contactDetails.body':
		'Nothing was charged. Check the email address and name on this order, correct them, and try again.',
	'payment.notice.authentication.headline': "Your bank didn't confirm the payment",
	'payment.notice.authentication.body':
		'Nothing was charged. Try again and complete the check your bank shows, or use a different card.',
	'payment.notice.bankDeclined.headline': 'Your bank declined the payment',
	'payment.notice.bankDeclined.body':
		'Nothing was charged. Try a different card, or ask your bank why this one was declined.',
	'payment.notice.cardLocked.headline': 'That card is locked',
	'payment.notice.cardLocked.body':
		"Nothing was charged. Your bank has locked this card's PIN, so only they can unlock it — use a different card to finish your order.",
	'payment.notice.limitReached.headline': 'That card has reached a limit',
	'payment.notice.limitReached.body':
		'Nothing was charged. This card has hit a spending limit set by your bank. Try again later, or use a different card.',
	'payment.notice.cardNotActivated.headline': "That card hasn't been activated yet",
	'payment.notice.cardNotActivated.body':
		'Nothing was charged. Activate the card with your bank, or use a different card to finish your order.',
	'payment.notice.cardNotSupported.headline': "That card can't be used here",
	'payment.notice.cardNotSupported.body': 'Nothing was charged. Try a different card.',
	'payment.notice.technical.headline': 'Something went wrong on our side',
	'payment.notice.technical.body': 'Nothing was charged, and nothing is wrong with your card. Try again in a moment.',
	'payment.notice.refused.headline': "Your payment didn't go through",
	'payment.notice.refused.body': 'Nothing was charged. Try again, or use a different card.',
	'payment.notice.orderStale.headline': 'This checkout is no longer open',
	'payment.notice.orderStale.body':
		'Nothing was charged. This order has already been finished or cancelled, so trying it again here will not work — start a new checkout to pay.',
	'payment.notice.blocked.headline': "We can't take a payment right now",
	'payment.notice.blocked.body':
		"The payment form didn't load. Check your connection and try again — nothing was charged.",
	'payment.notice.blocked.remedy': 'Reload payment form',
	// <schmancy-link>. A link that leaves the current page for a new tab says
	// so in its accessible name: a sighted reader sees the new tab arrive,
	// while a screen-reader user is left in a document whose Back has silently
	// stopped working. Leads with a space because it is appended to the run's
	// own text and the two would otherwise run together as one word.
	'link.newWindow': 'opens in a new window',
	// <schmancy-dropzone> and <schmancy-attachments>. The sentence a refused file
	// earns — a transient notice about ONE file, distinct from
	// `formField.error.*`, which is the field's own validity. Read when the
	// refusal fires rather than at render, so the words hold the language current
	// at the drop; a swap afterwards reaches the next refusal and leaves a notice
	// already on screen in the outgoing language, the same limitation
	// `payment.notice.*` above carries for the same reason.
	//
	// A single wildcard family is a thing the reader has a word for, so it is
	// named. Any other `accept` has to name the formats, and `{{formats}}` is
	// what `accept` MEANS rather than what it says — PNG, JPEG, PDF, deduped —
	// because the reader dropped a photo and `image/jpeg,image/png` answers a
	// question about MIME registration that nobody asked.
	'dropzone.add': 'Add',
	'dropzone.refusal.image': '{{name}} is not an image.',
	'dropzone.refusal.video': '{{name}} is not a video.',
	'dropzone.refusal.audio': '{{name}} is not audio.',
	'dropzone.refusal.text': '{{name}} is not a text file.',
	'dropzone.refusal.type': '{{name}} is not one of the accepted formats ({{formats}}).',
	'dropzone.refusal.tooLarge': '{{name}} is {{size}} kB — over the {{limit}} kB limit.',
	// One key per plural form, per `dateRangeInline.error.minGap.*` above: a
	// count the component inflects for English inflects for nothing else.
	'dropzone.refusal.tooMany.one': 'Only one file fits here — remove the current one first.',
	'dropzone.refusal.tooMany.other': 'Only {{max}} files fit here.',
	// <schmancy-attachments>. The trigger's own label — the button variant's
	// visible text, the zone's accessible name — used only where the consumer
	// gave the field no `label` of its own to say which image this one is. One
	// image per press whatever `max` is, so it never inflects.
	'attachments.attach': 'Attach an image',
	// <schmancy-attachments max="1">. A single-value field takes the first
	// image of a gesture in place of what it holds; the rest of that same
	// gesture name no destination, so each is turned away by its own name
	// rather than silently overwriting the one before it. Not a
	// `dropzone.refusal.tooMany.*` — nothing here is full, and there is
	// nothing to remove first.
	'attachments.refusal.oneAtATime': 'One image at a time — {{name}} was not attached.',
	// <schmancy-form>. The submit that got no answer before its own timeout ran
	// out. It says the send MAY have gone through, because the form cannot tell a
	// lost request from a slow one, and a reader told it failed sends a second
	// copy of whatever the first one did.
	'form.unanswered': 'No answer came back in time. This may still have gone through — check before sending it again.',
	// <schmancy-filter-chip>, <schmancy-input-chip>, and the thumbnails in
	// <schmancy-attachments>. The remove control's whole accessible name, one
	// wording moving all three — per `dateRangeInline`'s endpoint labels above, a
	// thing you remove from a row is that whichever row it sits in. `{{name}}` is
	// the subject the control removes: the text a chip renders, the filename an
	// attachment carries. So a row of them answers to one name each instead of all
	// to the same one; on the chips, `remove-label` is still the per-instance
	// override and still replaces the whole name. The subject rides inside the
	// sentence rather than being concatenated onto a translated verb, because a
	// language that puts the object first can only say so if it owns the sentence.
	'chip.remove': 'Remove {{name}}',
	// The same control on a chip rendering no text and carrying no value — an
	// icon-only or avatar-only chip, which holds no subject to be named by. An
	// attachment always has a filename, so it never reaches this one.
	'chip.removeUnnamed': 'Remove',
	// <schmancy-tree>. The disclosure control's whole accessible name. `{{name}}`
	// is the node's `label`, falling back to the text its `root` slot renders, so a
	// tree of twenty nodes answers to twenty names rather than twenty `Expand`s.
	// Two verbs rather than one sentence with a `{{verb}}` slot: a language that
	// inflects the node's name differently for the two actions can only say so if
	// each action owns its own sentence.
	'tree.expand': 'Expand {{name}}',
	'tree.collapse': 'Collapse {{name}}',
	// A node whose `root` slot renders no text and which was given no `label`.
	'tree.expandUnnamed': 'Expand',
	'tree.collapseUnnamed': 'Collapse',
	// <schmancy-copy>. `subject` names WHAT is being copied, never the control's
	// kind, so a page carrying five of these answers to five names rather than
	// five `Copy`s. The failure wording is the reason this component exists as a
	// primitive: it is the sentence a member reads when the value they believe
	// they hold is not in fact on their clipboard, and it therefore carries the
	// value itself — an unreachable clipboard must still leave the thing
	// transcribable, because a DNS record typed from memory fails silently.
	'copy.action': 'Copy {{subject}}',
	'copy.actionUnnamed': 'Copy',
	'copy.done': 'Copied',
	'copy.announce': '{{subject}} copied',
	'copy.announceUnnamed': 'Copied',
	'copy.failed': 'Could not copy {{subject}} — copy it by hand: {{value}}',
	'copy.failedUnnamed': 'Could not copy — copy it by hand: {{value}}',
	// The document reader names its invariant Source control by the document subject.
	'reader.measure.words.one': '{{count}} word',
	'reader.measure.words.other': '{{count}} words',
	'reader.view.source': 'Source',
	'reader.view.sourceNamed': 'Source of {{subject}}',
	// <schmancy-markdown>. An image is named, never fetched: a URL harvested out
	// of a third-party document is not a resource this library asks a reader's
	// browser to load.
	'markdown.image': 'Image: {{alt}}',
	'markdown.imageUnnamed': 'Image',
	'markdown.json': 'JSON',
	'markdown.diagramFailed': 'This diagram could not be rendered. Its source is shown below.',
	// <schmancy-lightbox>. `labels` names individual works while `label` is the
	// fallback for a one-image viewer or a gallery whose items share a name.
	'lightbox.viewer': 'Image viewer',
	'lightbox.image': 'Image',
	'lightbox.loading': 'Loading {{label}}',
	'lightbox.unavailable': 'Image unavailable',
	'lightbox.close': 'Close viewer',
	'lightbox.previous': 'Previous image',
	'lightbox.next': 'Next image',
	'lightbox.zoomOut': 'Zoom out',
	'lightbox.zoomIn': 'Zoom in',
	'lightbox.resetZoom': 'Reset zoom',
	'lightbox.position': '{{current}} of {{total}}',
	'lightbox.announce': '{{label}}, {{current}} of {{total}}',
	// Announced only when a zoom gesture settles (double-tap, pinch release, keyboard
	// +/-), never per-frame — a live region that fires on every pinch delta would
	// bury a screen reader user in noise instead of telling them anything.
	'lightbox.zoomLevel': '{{percent}}% zoom',
	// Read once via `aria-describedby`, since none of the gestures it names —
	// pinch, double-tap, swipe — are otherwise discoverable to an AT user, and the
	// visible chrome that used to announce them (button row) is gone by design.
	'lightbox.instructions':
		'Pinch or press plus and minus to zoom, double-tap to toggle zoom, drag to pan when zoomed, swipe or use arrow keys to browse, throw the picture in any direction, tap beside it, or press escape to close.',
	// <schmancy-navigation-bar-item> carrying neither `label` nor `icon` — the one
	// destination with nothing of its own to be named by.
	'navigationBar.item': 'Navigation item',
	'navigationBar.hasNotifications': 'Has notifications',
	// <schmancy-navigation-rail>, whose two directions draw two different
	// surfaces and so answer to two names. The `column` landmark's default is
	// written onto the host in `connectedCallback`, only where the consumer named
	// neither `aria-label` nor `aria-labelledby`, so it holds the language current
	// at mount and a swap afterwards leaves a rail already on screen answering in
	// the outgoing one. The column's scrollport reads this same key rather than
	// minting a second, since a port spelling the landmark's name differently is
	// exactly the ambiguity naming either one removes. A `row` draws a tab strip
	// instead of a landmark and says less of itself; it reads at render, so it
	// follows a swap.
	'navigationRail.landmark': 'Main navigation',
	'navigationRail.tablist': 'Navigation',
	// <schmancy-breadcrumb>'s navigation landmark, where the consumer named it
	// nothing through `aria-label`. Read at render, so it follows a swap.
	'breadcrumb.landmark': 'Breadcrumb',
	// <schmancy-install-app> / <schmancy-install-steps>. One vocabulary per
	// install ROUTE, never one shared "install" sentence: iOS says Home Screen,
	// a Mac says Dock, and an Android browser without a WebAPK says Home screen
	// in a different menu — showing any of them another platform's words is the
	// defect this component exists to stop. The control's own label is a slot,
	// so these are only what an app that names nothing gets.
	'installApp.action': 'Install app',
	'installApp.actionSteps': 'How to install',
	'installApp.dismiss': 'Dismiss the install offer',
	'installApp.announce.browserPrompt': 'This app can be installed on this device.',
	'installApp.announce.iosShare': 'This app can be added to your Home Screen.',
	'installApp.announce.macosDock': 'This app can be added to your Dock.',
	'installApp.announce.menuShortcut': 'This app can be added to your Home screen.',
	'installApp.steps.title': 'Install this app',
	'installApp.steps.subtitle': 'Three steps, in the browser menu.',
	'installApp.steps.done': 'Done',
	// Split around the glyph rather than trailing it, so a translation puts the
	// mark where its own sentence names the control. Apple's wording, from
	// Apple's own instructions. Both halves are written so the sentence still
	// NAMES the control with the glyph removed — a schmancy-icon is aria-hidden
	// by construction, so whatever a reader who cannot see it hears is these two
	// strings run together, and "Tap the ... in the toolbar" would name nothing.
	'installApp.steps.iosShare.share.before': 'Tap the',
	'installApp.steps.iosShare.share.after': 'Share button in the browser toolbar',
	'installApp.steps.iosShare.choose': 'Choose Add to Home Screen',
	'installApp.steps.iosShare.confirm': 'Tap Add',
	'installApp.steps.macosDock.menu.before': 'Open the File menu, or the',
	'installApp.steps.macosDock.menu.after': 'Share button in the toolbar',
	'installApp.steps.macosDock.choose': 'Choose Add to Dock',
	'installApp.steps.macosDock.confirm': 'Name the app, then click Add',
	// Firefox for Android's own menu label, and the one every other
	// shortcut-only Android browser words within a word of.
	'installApp.steps.menuShortcut.menu': 'Open the browser menu',
	'installApp.steps.menuShortcut.choose': 'Choose Add to Home screen',
	'installApp.steps.menuShortcut.confirm': 'Confirm the name, then tap Add',
} as const

export type ComponentTextKey = keyof typeof componentTextDefaults

/** One language's wording. Every key is optional; an unset key renders English. */
export type ComponentTextOverrides = Partial<Record<ComponentTextKey, string>>

// Signal.State rather than a state() singleton: the slot needs exactly the one
// thing state() would bring — a read the render pass tracks — and none of the
// rest (storage tiers, immer writes, context scoping, and participation in
// clearStates, which a device-level wording table would immediately have to opt
// out of). Staying on the primitive state() is itself built on also keeps this
// module at the foundation layer, importable by the base class and the form-field
// mixin, whose own strings are next.
const overrides = new Signal.State<ComponentTextOverrides>({})

/**
 * Replaces the whole wording table — `{}` restores English. Never a per-key
 * merge: merging would strand the outgoing language's entries under every key
 * the incoming table leaves out, so a language switch has to be able to say
 * "this table, and nothing else". Mounted components re-render on the swap.
 */
export function setComponentText(table: ComponentTextOverrides): void {
	overrides.set(table)
}

/**
 * The current wording for `key`. Read it inside `render()` — or anything else
 * the render pass tracks — and the component re-renders when the table is
 * swapped.
 */
export function componentText(key: ComponentTextKey, params?: Record<string, string | number>): string {
	return interpolate(overrides.get()[key] ?? componentTextDefaults[key], params)
}
